import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeProductActiveAdminService,
  createProductAdminService,
  deleteProductAdminService,
  editProductAdminService,
  getCategoriesAdminService,
  getFieldsAdminService,
  getProductsAdminService,
} from "../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { convertFieldTypeToText } from "../../../utils/enum";

export const productAdminContext = createContext({
  products: [],
  setProducts: () => {},
  product: {},
  setProduct: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  type: "",
  setType: () => {},
  isActive: null,
  setIsActive: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetFields: () => {},
  handleGetCategories: () => {},
  handleGetProducts: () => {},
  handleCreateProduct: () => {},
  handleEditProduct: () => {},
  handleChangeProductActive: () => {},
  handleDeleteProduct: () => {},
});

const ProductAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const { category } = useParams();

  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isActive, setIsActive] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      const category = val?.category;

      setProduct(val);
      setValue("picture", val.picture);
      setValue("title", val.title);
      setValue("code", val.code);
      setValue("category", {
        value: category?.parent?._id || category?._id,
        label: category?.parent?.title || category?.title,
      });
      if (category?.parent) {
        setValue("subCategory", {
          value: category?._id,
          label: category?.title,
        });
      }
      setValue("type", val.type);
      setValue("price", val.price);
      setValue("renewalPeriod", val.renewalPeriod);
      setValue(
        "fields",
        val.fields?.map((item) => ({
          value: item._id,
          label: `${item.label} - ${item.name} - ${convertFieldTypeToText(
            item.type
          )} - ${item.isRequired ? "الزامی" : "اختیاری"}`,
          data: item,
        }))
      );
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setProduct({});
  };

  // Get Categories
  const handleGetCategories = async (filter, callback, categoryId) => {
    const objData = {
      filter,
      ...(categoryId && { categoryId }),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoriesAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.categories?.map((item) => ({
              value: item._id,
              label: item.title,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Fields
  const handleGetFields = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getFieldsAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.fields?.map((item) => ({
              value: item._id,
              label: `${item.label} - ${item.name} - ${convertFieldTypeToText(
                item.type
              )} - ${item.isRequired ? "الزامی" : "اختیاری"}`,
              data: item,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Products
  const handleGetProducts = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(category && { category }),
      ...(type && { type }),
      ...(isActive && { isActive }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProductsAdminService(objData);
      if (status === 200) {
        setProducts(data.result?.products);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Product
  const handleCreateProduct = async (productData) => {
    const objData = {
      ...productData,
      category: productData?.subCategory?.value || productData?.category?.value,
      fields: productData?.fields?.map((item) => item.data),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createProductAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetProducts();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Product
  const handleEditProduct = async (productData) => {
    const objData = {
      id: product._id,
      ...productData,
      category: productData?.subCategory?.value || productData?.category?.value,
      fields: productData?.fields?.map((item) => item.data),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editProductAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetProducts();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Product Active
  const handleChangeProductActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeProductActiveAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetProducts();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Product
  const handleDeleteProduct = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteProductAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetProducts();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <productAdminContext.Provider
      value={{
        products,
        setProducts,
        product,
        setProduct,
        filter,
        setFilter,
        sort,
        setSort,
        type,
        setType,
        start,
        setStart,
        end,
        setEnd,
        isActive,
        setIsActive,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetCategories,
        handleGetFields,
        handleGetProducts,
        handleCreateProduct,
        handleEditProduct,
        handleChangeProductActive,
        handleDeleteProduct,
      }}
    >
      {children}
    </productAdminContext.Provider>
  );
};

export default ProductAdminContext;
